/**
 * Subscription Service
 *
 * API calls for company subscription management (Platform Admin).
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

export interface CompanySubscriptionDetail {
  id: string;
  name: string;
  website: string | null;
  industry: string | null;
  company_size: string | null;
  is_active: boolean;
  created_at: string;
  subscription_tier: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  max_users: number | null;
  max_projects: number | null;
  bug_reporting_enabled: boolean;
  plan_name: string | null;
  plan_price_monthly: number | null;
  user_count: number;
  project_count: number;
  days_on_plan: number;
  days_remaining_trial: number | null;
  days_remaining_subscription: number | null;
}

export interface SubscriptionKPIs {
  total_companies: number;
  active_subscriptions: number;
  trial_companies: number;
  suspended_companies: number;
  cancelled_companies: number;
  expiring_trials_7days: number;
  tier_breakdown: { tier: string; count: number }[];
}

export interface UpdateSubscriptionInput {
  subscription_tier?: string;
  subscription_status?: string;
  trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
  max_users?: number | null;
  max_projects?: number | null;
  bug_reporting_enabled?: boolean;
}

interface PaginatedResponse {
  success: boolean;
  data: {
    companies: CompanySubscriptionDetail[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface KPIResponse {
  success: boolean;
  data: SubscriptionKPIs;
}

/**
 * Get all companies with subscription details
 */
export async function getCompanySubscriptions(
  params: { page?: number; limit?: number; search?: string; subscription_status?: string; subscription_tier?: string },
  token: string
): Promise<PaginatedResponse['data']> {
  const response = await axios.get<PaginatedResponse>(
    `${API_BASE_URL}/companies/admin/subscriptions`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params
    }
  );
  return response.data.data;
}

/**
 * Get subscription KPI aggregates
 */
export async function getSubscriptionKPIs(token: string): Promise<SubscriptionKPIs> {
  const response = await axios.get<KPIResponse>(
    `${API_BASE_URL}/companies/admin/subscriptions/kpis`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Update a company's subscription
 */
export async function updateCompanySubscription(
  companyId: string,
  data: UpdateSubscriptionInput,
  token: string
): Promise<any> {
  const response = await axios.put(
    `${API_BASE_URL}/companies/admin/subscriptions/${companyId}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

export default {
  getCompanySubscriptions,
  getSubscriptionKPIs,
  updateCompanySubscription
};
